/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { styles } from './themes/PictoChartStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';
import { BundleType } from '../resources/nls/bundle';
import { getItemAriaProps } from './PictoChartUtils';
import { Ref } from 'preact/hooks';
import { PictoChartItem } from './PictoChart.type';

export type PictoElementProps<K, D extends PictoChartItem<K>> = {
  item: D;
  children: ComponentChildren;
  row: number;
  column: number;
  totalCount: number;
  class?: string;
  activeId?: string;
  isHighlighted?: boolean;
  isSelected?: boolean;
  hasSelection?: boolean;
  itemIdx?: number;
  countIdx?: number;
  supportsSelection?: boolean;
  supportsAction?: boolean;
  translations?: BundleType;
  focusedItemRef?: Ref<HTMLDivElement>;
};

const PictoChartElement = <K, D extends PictoChartItem<K>>(props: PictoElementProps<K, D>) => {
  const isInteractive = props.supportsSelection || props.supportsAction;

  const { dimmedItemStyle, interactiveItemStyle } = styles;

  const itemClasses = classNames([
    isInteractive && interactiveItemStyle,
    !props.isHighlighted && dimmedItemStyle
  ]);
  const isFirstElement = props.countIdx === 0;
  const itemAriaProps =
    (isFirstElement &&
      getItemAriaProps(
        props.item.count || 1,
        props.totalCount,
        !!props.supportsSelection,
        props.item.label,
        props.translations,
        props.isSelected,
        props.hasSelection,
        props.item.accessibleLabel,
        props.activeId
      )) ||
    {};

  return (
    <div
      ref={isFirstElement ? props.focusedItemRef : undefined}
      id={props.activeId}
      data-idx={props.itemIdx}
      data-count-idx={props.countIdx}
      style={{
        gridRow: `${props.row + 1} / span ${props.item.rowSpan || 1}`,
        gridColumn: `${props.column + 1} / span ${props.item.columnSpan || 1}`
      }}
      class={itemClasses}
      {...itemAriaProps}>
      {props.children}
    </div>
  );
};

export { PictoChartElement };
